<template>
  <el-form
    v-if="creative"
    ref="form"
    :model="creative"
    :label-position="isDesktop ? 'left' : 'top'"
    :class="{ desktop: isDesktop }"
    label-width="200px"
    @submit.prevent
  >
    <el-form-item
      prop="name"
      :error="errors.name"
      :rules="{
        required: true,
        message: 'Please enter creative name',
        trigger: ['blur', 'change']
      }"
      label="Name"
    >
      <el-input
        v-model="creative.name"
        type="text"
        @update:modelValue="input"
      />
    </el-form-item>
    <el-form-item
      prop="vertical_id"
      :error="errors.vertical_id"
      :rules="{
        required: true,
        message: 'Please choose ad category',
        trigger: ['blur', 'change']
      }"
      label="Ad category"
    >
      <VerticalSelector
        v-model="creative.vertical_id"
        :disabled="verticalDisabled"
        @update:modelValue="input"
      />
    </el-form-item>
    <template v-if="videoType">
      <VideoSelector
        v-model="creative.video_source"
        :disabled="contentLocked"
      />
      <el-form-item
        v-if="VideoSource.File === creative.video_source"
        prop="videos"
        :error="errors.video"
        :rules="{
          required: !creative.video_preview,
          message: 'Can\'t be blank. Please upload a file',
          trigger: ['blur', 'change']
        }"
        label="Upload video file"
        size="small"
      >
        <FileUpload
          :disabled="contentLocked"
          :thumb="creative.video_preview"
          :multiple="multipleAssetAllowed"
          :tooltip="VIDEO_UPLOAD_TOOLTIP"
          :multiple-tooltip="MULTIPLE_UPLOAD_TOOLTIP"
          allowed-formats=".mp4"
          @update:modelValue="input"
          @handleChange="
            (file, ref, list) => handleFileChange(file, ref, list, 'video')
          "
        />
      </el-form-item>
      <el-form-item
        v-if="VideoSource.Url === creative.video_source"
        prop="remote_url"
        :error="errors.video"
        :rules="{
          required: true,
          message: 'Can\'t be blank',
          trigger: ['blur', 'change']
        }"
        label="Add video URL"
      >
        <RemoteVideoInput
          v-model="creative.remote_url"
          :disabled="contentLocked"
          @update:modelValue="input"
        />
      </el-form-item>
      <el-form-item
        v-if="VideoSource.Tag === creative.video_source"
        prop="vast_tag"
        :error="errors.video"
        :rules="{
          required: true,
          message: 'Can\'t be blank',
          trigger: ['blur', 'change']
        }"
        label="Add VAST tag"
      >
        <VastTagInput
          v-model="creative.vast_tag"
          :disabled="contentLocked"
          @update:modelValue="input"
        />
      </el-form-item>
      <el-form-item
        v-if="VideoSource.Tag !== creative.video_source"
        prop="skip_offset"
        :error="errors.skip_offset"
        :disabled="!!creative.id"
        :rules="{
          required: true,
          type: 'number',
          min: 0.1,
          max: 10,
          message: 'Skip offset should be 0.1 to 10',
          trigger: ['blur', 'change']
        }"
        label="Skip offset"
      >
        <SkipOffsetInput
          v-model="creative.skip_offset"
          @update:modelValue="input"
        />
      </el-form-item>
    </template>
    <el-form-item
      v-if="linkActive"
      prop="link_url"
      :error="errors.link_url"
      :label="linkLabel"
      :rules="{
        required: !(isStaff && creative.code),
        message: 'Wrong link',
        trigger: ['blur', 'change']
      }"
    >
      <LinkUrlInput
        v-model="creative.link_url"
        :disabled="contentLocked"
        @update:modelValue="input"
      />
    </el-form-item>
    <el-form-item
      v-if="extendedSettingsAllowed"
      :error="errors.impression_url"
      label="Impression tracking URL"
    >
      <el-input
        v-model="creative.impression_url"
        :disabled="updateProhibited"
        type="text"
        @update:modelValue="input"
      />
    </el-form-item>
    <MacrosInfo />
    <el-form-item v-if="isStaff" label="Code">
      <CodeInput v-model="creative.code" @update:modelValue="input" />
    </el-form-item>
    <template v-if="AdType.Banner === campaign.type_id">
      <el-form-item
        prop="images"
        :error="errors.image"
        label="Image"
        size="small"
        :rules="{
          required: !creative.image_preview && !(isStaff && creative.code),
          message: 'Can\'t be blank. Please upload a file',
          trigger: ['blur', 'change']
        }"
      >
        <FileUpload
          :disabled="contentLocked"
          :thumb="creative.image_preview"
          :multiple="multipleAssetAllowed"
          :tooltip="IMAGE_UPLOAD_TOOLTIP"
          :multiple-tooltip="MULTIPLE_UPLOAD_TOOLTIP"
          allowed-formats=".jpg, .jpeg, .png, .gif, .mp4"
          @update:modelValue="input"
          @handleChange="
            (file, ref, list) => handleFileChange(file, ref, list, 'image')
          "
        />
      </el-form-item>
    </template>
    <template v-if="AdType.InpagePush === campaign.type_id">
      <el-form-item
        prop="icons"
        :error="errors.icon"
        label="Icon"
        size="small"
        :rules="{
          required: !creative.icon_preview,
          message: 'Can\'t be blank. Please upload a file',
          trigger: ['blur', 'change']
        }"
      >
        <FileUpload
          :disabled="contentLocked"
          :thumb="creative.icon_preview"
          :multiple="multipleAssetAllowed"
          :tooltip="ICON_UPLOAD_TOOLTIP"
          :multiple-tooltip="MULTIPLE_UPLOAD_TOOLTIP"
          allowed-formats=".jpg, .jpeg, .png, .gif"
          @update:modelValue="input"
          @handleChange="
            (file, ref, list) => handleFileChange(file, ref, list, 'icon')
          "
        />
      </el-form-item>
      <el-form-item
        prop="title"
        :error="errors.title"
        :rules="{
          required: true,
          message: 'Can\'t be blank',
          trigger: ['blur', 'change']
        }"
        label="Title"
      >
        <el-input
          v-model="creative.title"
          :disabled="contentLocked"
          type="text"
          @update:modelValue="input"
        />
      </el-form-item>
      <el-form-item
        :error="errors.description"
        prop="description"
        :rules="{
          required: true,
          message: 'Can\'t be blank',
          trigger: ['blur', 'change']
        }"
        label="Description"
      >
        <el-input
          v-model="creative.description"
          :disabled="contentLocked"
          type="textarea"
          @update:modelValue="input"
        />
      </el-form-item>
    </template>
    <RefreshRateInput
      v-model="creative.refresh_rate"
      @update:modelValue="input"
    />
    <HideReferrerInput
      v-model="creative.hide_referrer"
      @update:modelValue="input"
    />
    <OnlyProxyTrafficInput
      v-model="creative.only_proxy_traffic"
      @update:modelValue="input"
    />
    <el-form-item>
      <el-collapse v-model="activePanels">
        <el-collapse-item title="Traffic filters" name="geo">
          <TrafficFilters
            v-model="creative"
            :set-default-values="!creative.id"
            @update="updateFilters"
          />
        </el-collapse-item>
        <el-collapse-item title="Block proxy types" name="limits">
          <BlockedProxyTypes
            v-model="creative.block_proxy_type_ids"
            :disabled="creative.only_proxy_traffic"
            @update:modelValue="input"
          />
        </el-collapse-item>
      </el-collapse>
    </el-form-item>
    <el-form-item :error="errors.cpm_calculation_type" label="Price model">
      <el-select v-model="creative.cpm_calculation_type">
        <el-option
          v-for="option in availableCpmTypeOptions"
          :key="option.value"
          :label="option.label"
          :value="option.value"
        />
      </el-select>
    </el-form-item>
    <AutoCpaSetting
      v-if="showAutoCpaSetting"
      v-model="creative.auto_cpa_setting_attributes"
      :creature-prices="creative.prices"
      :win-prices="creative.win_prices"
      :use-historical-prices="Boolean(creative.id)"
      @update="updateAutoCpaSetting"
      @recommendations-change="updateCountryRecommendations"
      @validation-state-change="updateCountryPricesValidationState"
    />
    <CountryPrices
      v-else
      v-model="creative.prices"
      :winPrices="creative.win_prices"
      :use-historical-prices="Boolean(creative.id)"
      :cpc="cpcBillingMode"
      @update:modelValue="input"
      @recommendations-change="updateCountryRecommendations"
      @validation-state-change="updateCountryPricesValidationState"
    />
    <el-form-item v-if="contentLocked">
      <el-alert
        type="info"
        effect="light"
        show-icon
        :closable="false"
        :title="creative.content_lock_reason"
      />
    </el-form-item>
    <el-form-item v-if="changeSendsToReview">
      <el-alert
        type="warning"
        effect="light"
        show-icon
        :closable="false"
        title="After saving, the creative will be sent to review because its content has changed"
      />
    </el-form-item>
    <el-form-item>
      <el-button
        type="primary"
        plain
        :disabled="loading"
        :icon="loading ? 'LoadingElementIcon' : ''"
        @click.prevent="onSubmit(false)"
      >
        {{ actionText }}
      </el-button>
      <el-button
        v-if="!creative.id"
        type="primary"
        plain
        :disabled="loading"
        :icon="loading ? 'LoadingElementIcon' : ''"
        @click.prevent="onSubmit(true)"
      >
        Create and send to review
      </el-button>
      <el-button plain :disabled="loading" @click="onCancel">
        <span>Cancel</span>
      </el-button>
    </el-form-item>
  </el-form>
</template>

<script lang="ts">
import { defineComponent, computed, ref, reactive, watch, h } from 'vue'
import LinkUrlInput from '@/apps/traforama/components/LinkUrlInput.vue'
import RefreshRateInput from '@/apps/traforama/components/RefreshRateInput.vue'
import HideReferrerInput from './HideReferrerInput.vue'
import TrafficFilters from '@/components/TrafficFilters.vue'
import BlockedProxyTypes from '@/components/BlockedProxyTypes.vue'
import CountryPrices from './CountryPrices.vue'
import FileUpload from '@/components/FileUpload.vue'
import VideoSelector from './VideoSelector.vue'
import VastTagInput from '@/apps/traforama/components/VastTagInput.vue'
import RemoteVideoInput from '@/apps/traforama/components/RemoteVideoInput.vue'
import SkipOffsetInput from '@/apps/traforama/components/SkipOffsetInput.vue'
import CodeInput from '@/apps/traforama/components/CodeInput.vue'
import MacrosInfo from './MacrosInfo.vue'
import VerticalSelector from './VerticalSelector.vue'
import OnlyProxyTrafficInput from './OnlyProxyTrafficInput.vue'
import {
  useFileUpload,
  IMAGE_UPLOAD_TOOLTIP,
  ICON_UPLOAD_TOOLTIP,
  VIDEO_UPLOAD_TOOLTIP,
  MULTIPLE_UPLOAD_TOOLTIP,
  PLURALIZED_ASSET_NAMES,
  UploadTypes
} from '@/hooks/useFileUpload'

import {
  CreativeValidationErrorsKey,
  AdType,
  VideoSource,
  CPMCalculationTypeOptions,
  CPMCalculationTypeOption,
  IAutoCpaSettingAttributes,
  ICountryCPM
} from '@/api/types'
import useValidation from '@/hooks/useValidation'
import useResize from '@/hooks/useResize'
import { scrollToElement } from '@/utils/scroll-to'
import useFetchCampaign from '@/hooks/useFetchCampaign'
import { useUserStore } from '@/store/modules/user'
import AutoCpaSetting from '@/apps/traforama/views/creatives/components/AutoCpaSetting.vue'
import { defaultAutoCpaSetting } from '@/apps/traforama/store/creatives'
import { VERTICAL_BLOCKED_STATES } from '@/apps/traforama/constants'
import { ElMessage, ElMessageBox } from 'element-plus'
import useRouter from '@/hooks/useRouter'
import { formatCpm } from '@/apps/traforama/views/creatives/utils/cpm'
import type { CountryCpmRecommendation } from '@/apps/traforama/views/creatives/utils/cpm'
import { bannerRatioError } from '@/apps/traforama/views/creatives/utils/bannerRatio'

interface CountryCpmWarning extends CountryCpmRecommendation {
  value: number
}

const CPM_WARNING_ITEMS_LIMIT = 10

const sizeMap: Record<AdType, number> = {
  [AdType.Popunder]: 5000,
  [AdType.Interstitial]: 5000,
  [AdType.Banner]: 20000,
  [AdType.InpagePush]: 20000,
  [AdType.Slider]: 20000,
  [AdType.Overlay]: 20000,
  [AdType.OutStream]: 20000
}

const ASSET_KEYS = ['image', 'video', 'icon']
const REVIEWED_CONTENT_KEYS = [
  'link_url',
  'vast_tag',
  'remote_url',
  'title',
  'description'
]
const REVIEWED_STATES = ['rejected', 'approved', 'paused']

export default defineComponent({
  name: 'CreativeForm',
  components: {
    AutoCpaSetting,
    LinkUrlInput,
    RefreshRateInput,
    HideReferrerInput,
    TrafficFilters,
    BlockedProxyTypes,
    CountryPrices,
    FileUpload,
    VideoSelector,
    VastTagInput,
    RemoteVideoInput,
    SkipOffsetInput,
    MacrosInfo,
    CodeInput,
    VerticalSelector,
    OnlyProxyTrafficInput
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    actionText: {
      type: String,
      default: 'Save'
    },
    trafficFiltersDefaultValues: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const userStore = useUserStore()

    const { isStaff } = userStore
    const { validationErrors } = useValidation(CreativeValidationErrorsKey)
    const errors = validationErrors()
    const { injectCampaign } = useFetchCampaign()
    const campaign = injectCampaign()
    const { isDesktop } = useResize()
    const creative = ref()
    creative.value = { ...props.modelValue }
    const activePanels = reactive<Array<string>>([])
    const form = ref(null)
    const countryRecommendations = ref<CountryCpmRecommendation[]>([])
    const countryPricesInvalid = ref(false)
    const { router } = useRouter()

    watch(
      (_: any) => props.modelValue,
      (val: any) => {
        creative.value = { ...val }
      }
    )

    watch(
      () => creative.value.video_source,
      (val: any) => {
        if (val === VideoSource.Tag) {
          creative.value.vertical_id = 20 // VAST
        }
      }
    )

    const contentLocked = computed(() => !!creative.value.content_lock_reason)

    const updateProhibited = computed(() => {
      return (
        creative.value.id &&
        creative.value.advert_state !== 'rejected' &&
        !userStore.staff
      )
    })

    const savedContent = REVIEWED_CONTENT_KEYS.map(
      (key) => props.modelValue[key]
    )

    const contentChanged = computed(() =>
      REVIEWED_CONTENT_KEYS.some(
        (key, index) => creative.value[key] !== savedContent[index]
      )
    )

    const assetReplaced = computed(() =>
      ASSET_KEYS.some((key) => creative.value[key] instanceof File)
    )

    const changeSendsToReview = computed(
      () =>
        creative.value.id &&
        !userStore.staff &&
        REVIEWED_STATES.includes(creative.value.advert_state) &&
        (contentChanged.value || assetReplaced.value)
    )

    const multipleAssetAllowed = computed(() => {
      return !creative.value.id
    })

    const extendedSettingsAllowed = computed(
      () =>
        !!userStore.extendedSettingsAllowed &&
        ![AdType.Popunder, AdType.Interstitial].includes(
          campaign?.value?.type_id
        )
    )

    const verticalDisabled = computed(
      () =>
        !!creative.value.id &&
        VERTICAL_BLOCKED_STATES.includes(creative.value.advert_state) &&
        !userStore.staff
    )

    // @ts-ignore: Object is possibly 'undefined'
    const adType = computed(() => campaign.value.type_id)

    const datasetSize = computed(() => {
      return sizeMap[adType.value] ?? 0
    })

    // @ts-ignore: Object is possibly 'undefined'
    if (!creative.value.id && campaign.value) {
      // AutoCPASettings могут быть пустыми, если креатив не на AutoCPA
      // В этом случае поля отправляются, но в базе не сохраняются, и соответственно тут пустые
      // Перед попыткой изменить их значение проверяем, что они не пустые
      if (!creative.value.auto_cpa_setting_attributes) {
        creative.value.auto_cpa_setting_attributes = defaultAutoCpaSetting
      }
      creative.value.auto_cpa_setting_attributes.representative_dataset =
        datasetSize
    }

    const availableCpmTypeOptions = computed<CPMCalculationTypeOption[]>(() => {
      const list = creative.value?.available_cpm_types ?? []
      const current = creative.value?.cpm_calculation_type

      const available = new Set<string>(list)
      if (current) {
        available.add(current)
      }
      if (![AdType.Banner, AdType.InpagePush].includes(adType.value)) {
        available.delete('auto_cpc')
      }

      return CPMCalculationTypeOptions.filter((o) => available.has(o.value))
    })

    const videoType = computed(() =>
      [AdType.Slider, AdType.Overlay, AdType.OutStream].includes(
        campaign?.value?.type_id
      )
    )

    const linkActive = computed(() => {
      if (!campaign?.value || !creative?.value) return true

      return !videoType.value || creative.value.video_source !== VideoSource.Tag
    })

    const linkLabel = computed(() =>
      [AdType.Banner, AdType.Overlay, AdType.Slider, AdType.OutStream].includes(
        campaign?.value?.type_id
      )
        ? 'Click URL'
        : 'Link URL'
    )

    const input = async (val: any) => {
      await context.emit('update:modelValue', creative.value)
    }

    const updateFilters = async (val: any) => {
      creative.value = { ...creative.value, ...val }
      await context.emit('update:modelValue', creative.value)
    }

    const updateAutoCpaSetting = async (
      cpaSetting: IAutoCpaSettingAttributes,
      prices: Array<ICountryCPM>
    ) => {
      creative.value.auto_cpa_setting_attributes = cpaSetting
      creative.value.prices = prices
      await context.emit('update:modelValue', creative.value)
    }

    const updateCountryRecommendations = (
      recommendations: CountryCpmRecommendation[]
    ) => {
      countryRecommendations.value = recommendations
    }

    const updateCountryPricesValidationState = (invalid: boolean) => {
      countryPricesInvalid.value = invalid
    }

    // Files are checked asynchronously, so a rejected one can still be in `list` when
    // a sibling's check finishes first
    const rejectedFiles = new WeakSet<File>()

    const bannerRatioCheckRequired = (type: UploadTypes) =>
      type === UploadTypes.image &&
      campaign?.value?.type_id === AdType.Banner &&
      !creative.value.code

    const assignAssets = (file: any, list: any[], type: UploadTypes) => {
      let assetKey = type as string
      let assetItems = file.raw

      if (multipleAssetAllowed.value) {
        assetKey = PLURALIZED_ASSET_NAMES[type]
        assetItems = list
          .map((item: any) => item.raw)
          .filter((raw: File) => !rejectedFiles.has(raw))
      }
      creative.value[assetKey] = assetItems
      context.emit('update:modelValue', creative.value)
    }

    const handleFileChange = async (
      file: any,
      ref: any,
      list: any[],
      type: UploadTypes
    ) => {
      if (!useFileUpload(file, ref, type, errors)) return

      const ratioError = bannerRatioCheckRequired(type)
        ? await bannerRatioError(file.raw, campaign?.value?.size_id)
        : null
      if (ratioError) {
        rejectedFiles.add(file.raw)
        ref.value.handleRemove(file)
        ElMessage.error({
          message: ratioError,
          showClose: true,
          duration: 10000
        })
        if (multipleAssetAllowed.value) assignAssets(file, list, type)
        return
      }

      if (list.length > 1 && !multipleAssetAllowed.value) list.shift()

      errors.value = {}
      assignAssets(file, list, type)
    }

    const countryCpmWarnings = computed<CountryCpmWarning[]>(() => {
      const prices = new Map<string, number>(
        (creative.value.prices as ICountryCPM[])
          .map((price) => [price.id, Number(price.value)] as const)
          .filter(([, value]) => Number.isFinite(value))
      )

      return countryRecommendations.value.flatMap((recommendation) => {
        const value = prices.get(recommendation.iso)
        if (
          value === undefined ||
          recommendation.recommended <= 0 ||
          value <= recommendation.recommended
        ) {
          return []
        }

        return [{ ...recommendation, value }]
      })
    })

    const showAutoCpaSetting = computed(() => {
      return creative.value.cpm_calculation_type === 'auto_cpa'
    })

    const cpcBillingMode = computed(() => {
      return creative.value.cpm_calculation_type === 'auto_cpc'
    })

    const cpmWarningMessage = () => {
      const visibleWarnings = countryCpmWarnings.value.slice(
        0,
        CPM_WARNING_ITEMS_LIMIT
      )
      const remainingWarnings =
        countryCpmWarnings.value.length - visibleWarnings.length

      return h('div', [
        h('p', 'Are you sure you want to set CPM above Recommended CPM?'),
        h(
          'ul',
          visibleWarnings.map((warning) =>
            h(
              'li',
              `${warning.country} (${warning.iso}): CPM $${formatCpm(
                warning.value
              )}, Recommended CPM $${formatCpm(warning.recommended)}`
            )
          )
        ),
        remainingWarnings > 0 ? h('p', `and ${remainingWarnings} others`) : null
      ])
    }

    const validateForm = () =>
      new Promise<boolean>((resolve) => {
        if (!form.value) return resolve(false)

        // @ts-ignore: Object is possibly 'null'.
        form.value.validate((valid: boolean) => resolve(valid))
      })

    const onSubmit = async function (withReview?: boolean) {
      const doSubmit = () => {
        switch (creative.value.video_source) {
          case VideoSource.File: {
            creative.value.vast_tag = ''
            creative.value.remote_url = ''
            break
          }
          case VideoSource.Url: {
            creative.value.video = ''
            creative.value.vast_tag = ''
            break
          }
          case VideoSource.Tag: {
            creative.value.video = ''
            creative.value.remote_url = ''
            break
          }
        }

        let subtypeId
        // @ts-ignore: Object is possibly 'undefined'
        switch (campaign.value.type_id) {
          case AdType.Popunder:
            subtypeId = 10
            break
          case AdType.Banner:
            subtypeId = 3
            break
        }
        creative.value.subtype_id = subtypeId
        creative.value.force_review = withReview || false

        context.emit('submit', {
          creative: creative.value
        })
      }

      const valid = await validateForm()
      if (!valid || countryPricesInvalid.value) {
        scrollToElement('.el-form-item.is-error')
        return
      }

      const skipCpmConfirmation = userStore.skipCpmCheck || cpcBillingMode.value

      if (!skipCpmConfirmation && countryCpmWarnings.value.length > 0) {
        try {
          await ElMessageBox.confirm(cpmWarningMessage(), 'Warning', {
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            type: 'warning'
          })
        } catch {
          return
        }
      }

      doSubmit()
    }

    const onCancel = () => {
      if (userStore.isProcessingOnboarding) {
        userStore.clearOnboardProcessingFlag()
      }

      router.push('/campaigns')
    }

    return {
      errors,
      onSubmit,
      isDesktop,
      AdType,
      campaign,
      VideoSource,
      linkActive,
      activePanels,
      form,
      updateProhibited,
      contentLocked,
      changeSendsToReview,
      isStaff,
      input,
      creative,
      updateFilters,
      videoType,
      availableCpmTypeOptions,
      updateAutoCpaSetting,
      showAutoCpaSetting,
      handleFileChange,
      linkLabel,
      verticalDisabled,
      multipleAssetAllowed,
      extendedSettingsAllowed,
      IMAGE_UPLOAD_TOOLTIP,
      MULTIPLE_UPLOAD_TOOLTIP,
      VIDEO_UPLOAD_TOOLTIP,
      ICON_UPLOAD_TOOLTIP,
      cpcBillingMode,
      updateCountryRecommendations,
      updateCountryPricesValidationState,
      onCancel
    }
  }
})
</script>
<style scoped lang="scss">
:deep(.el-collapse) {
  width: 100%;
}
:deep(.el-form-item) {
  scroll-margin-top: 150px;
}
</style>
